<template>
  <div class="upsert-order-can-not-serve">
    <KTCodePreview :title="pageTitle">
      <template v-slot:preview>
        <div v-if="loading" class="text-center">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="ml-2">Đang tải dữ liệu...</strong>
        </div>

        <div v-else>
          <b-form @submit.prevent="onSubmit">
            <!-- Customer Information Section -->
            <b-card no-body class="mb-4">
              <b-card-header class="bg-light">
                <h5 class="mb-0">
                  <i class="fas fa-user mr-2"></i>Thông tin khách hàng
                </h5>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <!-- Phone Number -->
                  <b-col md="6">
                    <b-form-group label-class="font-weight-bold">
                      <template v-slot:label>
                        Số điện thoại
                        <span class="text-danger">*</span>
                      </template>
                      <b-form-input
                        v-model="formData.customerPhone"
                        placeholder="Nhập số điện thoại"
                        size="sm"
                        trim
                        :state="validatePhone"
                      ></b-form-input>
                      <b-form-invalid-feedback :state="validatePhone">
                        Số điện thoại không hợp lệ
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>

                  <!-- Customer Name -->
                  <b-col md="6">
                    <b-form-group label-class="font-weight-bold">
                      <template v-slot:label>
                        Họ và tên
                        <span class="text-danger">*</span>
                      </template>
                      <b-form-input
                        v-model="formData.customerName"
                        placeholder="Nhập họ và tên khách hàng"
                        size="sm"
                        trim
                        :state="validateCustomerName"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <!-- Gender -->
                  <b-col md="6">
                    <b-form-group label-class="font-weight-bold">
                      <template v-slot:label>
                        Danh xưng
                        <span class="text-danger">*</span>
                      </template>
                      <b-form-select
                        v-model="formData.gender"
                        :options="genderOptions"
                        value-field="value"
                        text-field="text"
                        size="sm"
                        :state="validateGender"
                      >
                        <template v-slot:first>
                          <b-form-select-option :value="null" disabled>
                            -- Chọn danh xưng --
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </b-col>

                  <!-- Address -->
                  <b-col md="6">
                    <b-form-group label-class="font-weight-bold">
                      <template v-slot:label> Địa chỉ </template>
                      <b-form-input
                        v-model="formData.customerAddress"
                        placeholder="Nhập địa chỉ (không bắt buộc)"
                        size="sm"
                        trim
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Auto-fill Checkbox (Only for ADD mode) -->
                <b-row v-if="mode === MODE.ADD">
                  <b-col>
                    <b-form-checkbox
                      v-model="autoFillEnabled"
                      @change="toggleAutoFill"
                    >
                      <span class="text-muted">
                        Tự động điền thông tin mặc định
                      </span>
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Products Section -->
            <b-card no-body class="mb-4">
              <b-card-header class="bg-light">
                <h5 class="mb-0">
                  <i class="fas fa-box mr-2"></i>Sản phẩm không phục vụ được
                </h5>
              </b-card-header>
              <b-card-body>
                <!-- Product Search -->
                <b-row class="mb-3">
                  <b-col md="12">
                    <b-form-group label-class="font-weight-bold">
                      <template v-slot:label>
                        Tìm kiếm sản phẩm
                        <span class="text-danger">*</span>
                      </template>
                      <Treeselect
                        :load-options="onSearchProductChange"
                        :multiple="false"
                        :async="true"
                        placeholder="Nhập tên hoặc mã sản phẩm để tìm kiếm..."
                        :cache-options="false"
                        retryText="Thử lại..."
                        searchPromptText="Nhập để tìm kiếm..."
                        loadingText="Đang tải..."
                        noResultsText="Không có kết quả"
                        openDirection="bottom"
                        @select="onSelectProduct"
                        :value="null"
                      >
                        <label
                          slot="option-label"
                          slot-scope="{ node, labelClassName }"
                          :class="labelClassName"
                        >
                          <div>
                            <b class="text-primary">{{ node.raw.code }}</b>
                            <b
                              :class="
                                node.raw.quantityInStock > 0
                                  ? 'text-success'
                                  : 'text-danger'
                              "
                            >
                              ( TCB: {{ node.raw.quantityInStock }} )
                            </b>
                            {{ node.raw.name }}
                          </div>
                        </label>
                      </Treeselect>

                      <div class="mt-2">
                        <small class="text-muted">
                          <i class="fas fa-info-circle mr-1"></i>
                          Không tìm thấy sản phẩm?
                          <a
                            href="javascript:void(0)"
                            @click="showAddFreeTextProduct"
                            class="font-weight-bold"
                          >
                            Thêm sản phẩm không có trong hệ thống
                          </a>
                        </small>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Selected Products Table -->
                <b-row v-if="formData.products.length > 0">
                  <b-col>
                    <label class="font-weight-bold">
                      Danh sách sản phẩm đã chọn ({{
                        formData.products.length
                      }})
                    </label>
                    <b-table
                      :items="formData.products"
                      :fields="productFields"
                      class="table-bordered table-sm"
                      striped
                      hover
                    >
                      <template v-slot:cell(stt)="row">
                        <div class="text-center">{{ row.index + 1 }}</div>
                      </template>

                      <template v-slot:cell(productName)="row">
                        <div>
                          <strong>{{ row.item.productName }}</strong>
                          <br />
                          <small class="text-muted" v-if="row.item.productCode">
                            Mã: {{ row.item.productCode }}
                          </small>
                        </div>
                      </template>

                      <template v-slot:cell(reasonId)="row">
                        <b-form-select
                          v-model="row.item.reasonId"
                          :options="reasonOptions"
                          value-field="value"
                          text-field="text"
                          size="sm"
                        >
                          <template v-slot:first>
                            <b-form-select-option :value="null" disabled>
                              -- Chọn lý do --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                      </template>

                      <template v-slot:cell(actions)="row">
                        <div class="text-center">
                          <b-button
                            size="sm"
                            variant="danger"
                            @click="removeProduct(row.index)"
                            v-b-tooltip.hover
                            title="Xóa sản phẩm"
                          >
                            <i class="fas fa-trash"></i>
                          </b-button>
                        </div>
                      </template>
                    </b-table>
                  </b-col>
                </b-row>

                <!-- Empty State -->
                <b-row v-else>
                  <b-col>
                    <div class="text-center text-muted py-4">
                      <i class="fas fa-box-open fa-3x mb-3"></i>
                      <p>Chưa có sản phẩm nào được chọn</p>
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Description Section -->
            <b-card no-body class="mb-4">
              <b-card-header class="bg-light">
                <h5 class="mb-0">
                  <i class="fas fa-file-alt mr-2"></i>Ghi chú
                </h5>
              </b-card-header>
              <b-card-body>
                <b-form-group label-class="font-weight-bold">
                  <template v-slot:label> Mô tả chi tiết </template>
                  <b-form-textarea
                    v-model="formData.description"
                    placeholder="Nhập mô tả chi tiết về phiếu không phục vụ..."
                    :rows="4"
                    :max-rows="8"
                    size="sm"
                    trim
                  ></b-form-textarea>
                </b-form-group>
              </b-card-body>
            </b-card>

            <!-- Action buttons -->
            <div class="d-flex mt-5">
              <b-button variant="secondary" class="mr-3" @click="handleCancel">
                Hủy
              </b-button>
              <b-button variant="primary" type="submit" :disabled="submitting">
                <b-spinner v-if="submitting" small class="mr-2"></b-spinner>
                {{ mode === MODE.ADD ? 'Lưu phiếu' : 'Cập nhật' }}
              </b-button>
            </div>
          </b-form>
        </div>
      </template>
    </KTCodePreview>

    <!-- Modal Add Free-text Product -->
    <b-modal
      id="modal-add-free-product"
      title="Thêm sản phẩm không có trong hệ thống"
      @ok="handleAddFreeTextProduct"
      ok-title="Thêm"
      cancel-title="Hủy"
      @hidden="resetFreeProductModal"
    >
      <b-form-group label="Tên sản phẩm" label-class="font-weight-bold">
        <b-form-input
          v-model="freeProductName"
          placeholder="Nhập tên sản phẩm"
          trim
          ref="freeProductInput"
        ></b-form-input>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { ASYNC_SEARCH } from '@riophae/vue-treeselect';

import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { swalMixin } from '@/view/mixins';

import ApiService from '@/core/services/api.service';
import productService from '@/core/api/product';
import { ProductModel } from '@/core/models';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';

import * as ENUM_NAME from '@/utils/enum-name';
import * as ENUM from '@/utils/enum';
import OrderCanNotServeService from '@/core/api/order-cant-serve/order-cant-serve.service';
import { OrderCanNotServeModel } from '@/core/models/OrderCanNotServe.model';

const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  if (phone === '19002057') return true;
  return /(03|05|07|08|09|01[2|6|8|9])+([0-9]{8})\b/.test(phone);
};

const ROUTE_CONFIGS = {
  'create-order-cant-serve': {
    mode: ENUM.MODE.ADD,
    title: 'Tạo phiếu không phục vụ được',
  },
  'edit-order-cant-serve': {
    mode: ENUM.MODE.EDIT,
    title: 'Cập nhật phiếu không phục vụ được',
  },
};

export default {
  name: 'UpsertOrderCanNotServe',
  mixins: [swalMixin],
  components: {
    KTCodePreview,
    Treeselect,
  },

  data() {
    return {
      MODE: ENUM.MODE,
      loading: false,
      submitting: false,
      autoFillEnabled: false,

      formData: {
        id: null,
        customerPhone: '',
        customerName: '',
        customerAddress: '',
        gender: null,
        products: [],
        description: '',
      },

      productOptions: [],
      freeProductName: '',

      defaultCustomerInfo: {
        customerPhone: '19002057',
        customerName: 'VFXANH',
        gender: ENUM.GENDER.MALE,
      },

      productFields: [
        {
          key: 'stt',
          label: 'STT',
          thStyle: {
            textAlign: 'center',
            width: '60px',
          },
        },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          thStyle: { textAlign: 'center' },
        },
        {
          key: 'reasonId',
          label: 'Lý do không phục vụ',
          thStyle: {
            textAlign: 'center',
            width: '250px',
          },
        },
        {
          key: 'actions',
          label: 'Thao tác',
          thStyle: {
            textAlign: 'center',
            width: '100px',
          },
        },
      ],

      genderOptions: this.getOptions(
        ENUM.CUSTOMER_APPELLATION,
        ENUM_NAME.CUSTOMER_APPELLATION_NAME,
      ),
      reasonOptions: this.getOptions(
        ENUM.ORDER_CANT_SERVE_REASON,
        ENUM_NAME.ORDER_CANT_SERVE_REASON_NAME,
      ),
    };
  },

  computed: {
    mode() {
      const routeName = this.$route.name;
      return ROUTE_CONFIGS[routeName]?.mode || this.MODE.ADD;
    },

    pageTitle() {
      const routeName = this.$route.name;
      return ROUTE_CONFIGS[routeName]?.title || 'Phiếu không phục vụ được';
    },

    validatePhone() {
      if (!this.formData.customerPhone) return null;
      return isValidPhoneNumber(this.formData.customerPhone);
    },

    validateGender() {
      if (!this.formData.gender) return null;
      return this.formData.gender !== null;
    },

    validateCustomerName() {
      if (!this.formData.customerName) return null;
      return this.formData.customerName.trim().length > 0;
    },

    hasProductWithoutReason() {
      return this.formData.products.some((p) => !p.reasonId);
    },

    selectedProductIds() {
      return this.formData.products
        .filter((p) => p.productId)
        .map((p) => p.productId);
    },
  },

  async mounted() {
    await this.setBreadCrumbs();

    this.formData.id = this.$route.params.id;

    if (this.mode !== this.MODE.ADD && this.formData.id) {
      await this.loadOrder();
    }
  },

  methods: {
    makeToastSuccess,
    makeToastFaile,

    async setBreadCrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Báo cáo', route: '/reports' },
        { title: 'Phiếu không phục vụ được', route: '/order-cant-serve' },
        { title: this.pageTitle, active: true },
      ]);
    },

    getOptions(enumValues, enumNames) {
      if (!enumValues || !enumNames) return [];

      return Object.entries(enumNames)
        .filter(([key]) => key !== 'null' && enumNames[key])
        .map(([key, value]) => ({
          value: Number(key),
          text: value,
        }));
    },

    async loadOrder() {
      this.loading = true;
      try {
        const response = await OrderCanNotServeService.detail(this.formData.id);
        const orderData = OrderCanNotServeModel.fromJson(response.data.data);

        this.formData = {
          id: orderData.id,
          customerPhone: orderData.customerPhone || '',
          customerName: orderData.customerName || '',
          customerAddress: orderData.customerAddress || '',
          gender: orderData.gender,
          description: orderData.description || '',
          products: orderData.products.map((p) => ({
            productId: p.productId || null,
            productName: p.productName || '',
            reasonId: p.reasonId,
            description: p.description || null,
          })),
        };
      } catch (error) {
        console.error('Error loading order:', error);
        makeToastFaile('Không thể tải thông tin phiếu');
        this.$router.push({ name: 'order-cant-serve' });
      } finally {
        this.loading = false;
      }
    },

    toggleAutoFill(enabled) {
      if (enabled) {
        this.formData.customerPhone = this.defaultCustomerInfo.customerPhone;
        this.formData.customerName = this.defaultCustomerInfo.customerName;
        this.formData.gender = this.defaultCustomerInfo.gender;
      } else {
        this.formData.customerPhone = '';
        this.formData.customerName = '';
        this.formData.gender = null;
      }
    },

    async onSearchProductChange({ action, searchQuery, callback }) {
      if (action === ASYNC_SEARCH) {
        await this.searchProducts(searchQuery);
        callback(null, this.productOptions);
      }
    },

    async searchProducts(searchQuery) {
      this.productOptions = [];

      if (!searchQuery) return;

      try {
        const { data } = await productService.searchProducts({
          productName: searchQuery,
          limit: 10,
          isCheckStock: false,
          excludeProductIds: this.selectedProductIds,
        });

        const products = ProductModel.fromJsonArray(data?.data) || [];
        this.productOptions = products.map((product) => ({
          id: product.id,
          name: product.name,
          label: product.name,
          code: product.code,
          quantityInStock:
            product.productStock?.reduce(
              (sum, stock) => sum + stock.quantity,
              0,
            ) || 0,
        }));
      } catch (error) {
        console.error('Error loading products:', error);
      }
    },

    onSelectProduct(selectedProduct) {
      if (!selectedProduct) return;

      const existingProduct = this.formData.products.find(
        (p) => p.productId === selectedProduct.id,
      );

      if (existingProduct) {
        makeToastFaile('Sản phẩm đã được thêm vào danh sách');
        return;
      }

      this.formData.products.push({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        productCode: selectedProduct.code,
        reasonId: null,
        description: null,
      });

      makeToastSuccess(`Đã thêm sản phẩm: ${selectedProduct.name}`);
      this.productOptions = [];
    },

    showAddFreeTextProduct() {
      this.$bvModal.show('modal-add-free-product');
      this.$nextTick(() => {
        this.$refs.freeProductInput?.focus();
      });
    },

    handleAddFreeTextProduct(bvModalEvt) {
      bvModalEvt.preventDefault();

      const productName = this.freeProductName.trim();

      if (!productName) {
        makeToastFaile('Vui lòng nhập tên sản phẩm');
        return;
      }

      const exists = this.formData.products.some(
        (p) => p.productName.toLowerCase() === productName.toLowerCase(),
      );

      if (exists) {
        makeToastFaile('Sản phẩm đã được thêm vào danh sách');
        return;
      }

      this.formData.products.push({
        productId: null,
        productName: productName,
        productCode: null,
        reasonId: null,
        description: null,
      });

      makeToastSuccess(`Đã thêm sản phẩm: ${productName}`);
      this.$bvModal.hide('modal-add-free-product');
    },

    resetFreeProductModal() {
      this.freeProductName = '';
    },

    removeProduct(index) {
      const product = this.formData.products[index];
      this.formData.products.splice(index, 1);
      makeToastSuccess(`Đã xóa sản phẩm: ${product.productName}`);
    },

    validateForm() {
      if (!this.formData.customerPhone) {
        makeToastFaile('Vui lòng nhập số điện thoại');
        return false;
      }

      if (!this.validatePhone) {
        makeToastFaile('Số điện thoại không hợp lệ');
        return false;
      }

      if (!this.formData.customerName || !this.formData.customerName.trim()) {
        makeToastFaile('Vui lòng nhập tên khách hàng');
        return false;
      }

      if (!this.formData.gender && this.formData.gender !== 0) {
        makeToastFaile('Vui lòng chọn danh xưng');
        return false;
      }

      if (this.formData.products.length === 0) {
        makeToastFaile('Vui lòng thêm ít nhất một sản phẩm');
        return false;
      }

      if (this.hasProductWithoutReason) {
        makeToastFaile('Vui lòng chọn lý do cho tất cả sản phẩm');
        return false;
      }

      return true;
    },

    async onSubmit() {
      if (!this.validateForm()) return;

      const result = await this.popupSwalConfirm({
        title:
          this.mode === this.MODE.ADD
            ? 'Xác nhận tạo phiếu'
            : 'Xác nhận cập nhật',
        text:
          this.mode === this.MODE.ADD
            ? 'Bạn có chắc chắn muốn tạo phiếu không phục vụ này?'
            : 'Bạn có chắc chắn muốn cập nhật phiếu này?',
      });

      if (!result.isConfirmed) return;

      this.submitting = true;

      try {
        const payload = {
          customerId: this.formData.customerId || null,
          customerPhone: this.formData.customerPhone?.trim() || null,
          customerName: this.formData.customerName?.trim() || null,
          customerAddress: this.formData.customerAddress?.trim() || null,
          gender: this.formData.gender,
          description: this.formData.description?.trim() || null,
          products: this.formData.products.map((p) => ({
            productId: p.productId || null,
            productName: p.productName?.trim() || null,
            reasonId: p.reasonId,
            description: p.description?.trim() || null,
          })),
        };

        ApiService.setHeader();

        if (this.mode === this.MODE.ADD) {
          await ApiService.post('order-cant-serve', payload);
          makeToastSuccess('Tạo phiếu thành công');
        } else {
          await ApiService.put(`order-cant-serve/${this.formData.id}`, payload);
          makeToastSuccess('Cập nhật phiếu thành công');
        }

        this.$router.push({ name: 'order-cant-serve' });
      } catch (error) {
        console.error('Error saving order:', error);
        makeToastFaile(
          error.response?.data?.message ||
            `Có lỗi xảy ra khi ${
              this.mode === this.MODE.ADD ? 'tạo' : 'cập nhật'
            } phiếu`,
        );
      } finally {
        this.submitting = false;
      }
    },

    async handleCancel() {
      const result = await this.popupSwalWarning({
        title: 'Hủy thao tác',
        text: 'Dữ liệu đã nhập sẽ không được lưu. Bạn có chắc chắn muốn hủy?',
      });

      if (result.isConfirmed) {
        this.$router.push({ name: 'order-cant-serve' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.upsert-order-can-not-serve {
  .card-header {
    h5 {
      color: #181c32;
      font-weight: 600;
    }
  }

  .form-group label {
    font-weight: 500;
  }

  .table th,
  .table td {
    padding: 0.75rem;
    vertical-align: middle;
  }
}
</style>

<style lang="scss">
.vue-treeselect__input-container {
  padding-top: 0px;
}

.vue-treeselect__control {
  border-radius: 0.28rem;
  border: 1px solid #ced4da;
  height: 2.5rem;
}

.vue-treeselect--has-value .vue-treeselect__multi-value {
  margin-bottom: 2px;
}
</style>
